import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import { route } from 'preact-router';

import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

function RaceLayer({ races }) {
    const map = useMap();

    const markersRef = { current: [] };
    const spiderfiedRef = { current: null };
    const spiderfyTimestamp = { current: 0 };

    const clearMarkers = () => {
        markersRef.current.forEach(m => map.removeLayer(m));
        markersRef.current = [];
    };

    const unspiderfy = () => {
        if (!spiderfiedRef.current) return;
        spiderfiedRef.current.markers.forEach(m => map.removeLayer(m));
        spiderfiedRef.current.lines.forEach(l => map.removeLayer(l));
        spiderfiedRef.current = null;
    };

    const createRaceMarkerHtml = (race, markerSize) => {
        const img = race.images?.[0];
        const imgUrl = img ? (img.s3SmallLink || img.s3MediumLink || '') : '';
        if (imgUrl) {
            return `<div style="
                width: ${markerSize}px;
                height: ${markerSize}px;
                border-radius: 8px;
                border: 3px solid #ffc107;
                box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                overflow: hidden;
                position: relative;
                background: #eee;
            ">
                <img src="${imgUrl}" style="
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                " loading="lazy" />
                <div style="
                    position: absolute;
                    bottom: -4px;
                    right: -4px;
                    background: #ffc107;
                    color: #000;
                    border-radius: 50%;
                    width: 20px;
                    height: 20px;
                    font-size: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border: 2px solid white;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.3);
                "><i class="fa-solid fa-flag-checkered"></i></div>
            </div>`;
        }
        return `<div style="
            width: ${markerSize}px;
            height: ${markerSize}px;
            border-radius: 8px;
            border: 3px solid #ffc107;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
            background: #6c757d;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #ffc107;
            font-size: 18px;
            position: relative;
        ">
            <i class="fa-solid fa-flag-checkered"></i>
        </div>`;
    };

    const spiderfy = (group, centerLat, centerLng) => {
        unspiderfy();

        const count = group.length;
        const centerPoint = map.latLngToContainerPoint([centerLat, centerLng]);
        const spiderMarkers = [];
        const spiderLines = [];
        const markerSize = 45;

        group.forEach((race, i) => {
            let offsetX, offsetY;

            if (count <= 12) {
                const radius = count <= 5 ? 60 : (count <= 10 ? 80 : 100);
                const angle = (i / count) * 2 * Math.PI - Math.PI / 2;
                offsetX = radius * Math.cos(angle);
                offsetY = radius * Math.sin(angle);
            } else {
                const angle = i * 0.8;
                const radius = 50 + i * 8;
                offsetX = radius * Math.cos(angle);
                offsetY = radius * Math.sin(angle);
            }

            const targetPoint = L.point(centerPoint.x + offsetX, centerPoint.y + offsetY);
            const targetLatLng = map.containerPointToLatLng(targetPoint);

            const line = L.polyline(
                [[centerLat, centerLng], targetLatLng],
                { color: '#999', weight: 1, opacity: 0.5 },
            );
            line.addTo(map);
            spiderLines.push(line);

            const html = createRaceMarkerHtml(race, markerSize);
            const icon = L.divIcon({
                html,
                className: 'feed-map-marker',
                iconSize: [markerSize, markerSize],
                iconAnchor: [markerSize / 2, markerSize / 2],
            });

            const marker = L.marker(targetLatLng, { icon });
            marker.on('click', (e) => {
                L.DomEvent.stopPropagation(e);
                route(`/race/${race.id}`);
            });
            marker.addTo(map);
            spiderMarkers.push(marker);
        });

        spiderfiedRef.current = {
            markers: spiderMarkers,
            lines: spiderLines,
        };
        spiderfyTimestamp.current = Date.now();
    };

    const buildClusters = () => {
        if (spiderfiedRef.current && Date.now() - spiderfyTimestamp.current < 500) {
            return;
        }
        unspiderfy();
        clearMarkers();

        if (!races || races.length === 0) return;

        let bounds;
        try {
            bounds = map.getBounds();
        } catch (e) {
            return;
        }
        if (!bounds.isValid()) return;

        const cellSize = 80;

        const visibleRaces = races.filter(r => {
            const lat = r.startLatlng?.[0];
            const lng = r.startLatlng?.[1];
            if (typeof lat !== 'number' || typeof lng !== 'number' || !isFinite(lat) || !isFinite(lng)) return false;
            return bounds.contains([lat, lng]);
        });

        const cells = {};
        visibleRaces.forEach(race => {
            const lat = race.startLatlng[0];
            const lng = race.startLatlng[1];
            const point = map.latLngToContainerPoint([lat, lng]);
            const cellX = Math.floor(point.x / cellSize);
            const cellY = Math.floor(point.y / cellSize);
            const key = `${cellX}_${cellY}`;
            if (!cells[key]) {
                cells[key] = [];
            }
            cells[key].push(race);
        });

        Object.values(cells).forEach(group => {
            const representative = group.find(r => {
                const img = r.images?.[0];
                return img && (img.s3SmallLink || img.s3MediumLink);
            }) || group[0];

            const lat = representative.startLatlng[0];
            const lng = representative.startLatlng[1];
            const count = group.length;
            const size = count > 1 ? 60 : 50;

            const baseHtml = createRaceMarkerHtml(representative, size);

            let html = baseHtml;
            if (count > 1) {
                const badge = `<div style="
                    position: absolute;
                    top: -6px;
                    right: -6px;
                    background: #0d6efd;
                    color: white;
                    border-radius: 50%;
                    width: 22px;
                    height: 22px;
                    font-size: 11px;
                    font-weight: bold;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border: 2px solid white;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.3);
                ">${count}</div>`;
                // Insert badge before the closing </div>
                const lastClose = html.lastIndexOf('</div>');
                html = html.slice(0, lastClose) + badge + html.slice(lastClose);
            }

            const icon = L.divIcon({
                html,
                className: 'feed-map-marker',
                iconSize: [size, size],
                iconAnchor: [size / 2, size / 2],
            });

            const marker = L.marker([lat, lng], { icon });

            marker.on('click', (e) => {
                L.DomEvent.stopPropagation(e);
                if (count > 1) {
                    const currentZoom = map.getZoom();

                    if (currentZoom < 12) {
                        map.setView([lat, lng], Math.min(currentZoom + 3, 18));
                    } else {
                        const clusterBounds = L.latLngBounds(
                            group.map(r => [r.startLatlng[0], r.startLatlng[1]]),
                        );
                        const ne = clusterBounds.getNorthEast();
                        const sw = clusterBounds.getSouthWest();
                        const boundsAreaZero = (
                            Math.abs(ne.lat - sw.lat) < 0.00001
                            && Math.abs(ne.lng - sw.lng) < 0.00001
                        );

                        if (boundsAreaZero || currentZoom >= 14) {
                            spiderfy(group, lat, lng);
                        } else {
                            const targetZoom = map.getBoundsZoom(clusterBounds, false, [50, 50]);
                            if (targetZoom <= currentZoom + 1) {
                                map.setView([lat, lng], Math.min(currentZoom + 3, 18));
                            } else {
                                map.fitBounds(clusterBounds, { padding: [50, 50], maxZoom: 18 });
                            }
                        }
                    }
                } else {
                    route(`/race/${representative.id}`);
                }
            });

            marker.addTo(map);
            markersRef.current.push(marker);
        });
    };

    const onMapClick = () => {
        if (spiderfiedRef.current && Date.now() - spiderfyTimestamp.current < 500) return;
        unspiderfy();
    };
    map.off('click', onMapClick);
    map.on('click', onMapClick);

    map.off('zoomend moveend', buildClusters);
    map.on('zoomend moveend', buildClusters);

    setTimeout(() => buildClusters(), 100);

    return null;
}

function NewsLayer({ news }) {
    const map = useMap();

    const markersRef = { current: [] };
    const spiderfiedRef = { current: null };
    const spiderfyTimestamp = { current: 0 };

    const clearMarkers = () => {
        markersRef.current.forEach(m => map.removeLayer(m));
        markersRef.current = [];
    };

    const unspiderfy = () => {
        if (!spiderfiedRef.current) return;
        spiderfiedRef.current.markers.forEach(m => map.removeLayer(m));
        spiderfiedRef.current.lines.forEach(l => map.removeLayer(l));
        spiderfiedRef.current = null;
    };

    const createNewsMarkerHtml = (newsItem, markerSize) => {
        const img = newsItem.images?.[0] || newsItem.files?.[0];
        const imgUrl = img ? (img.s3SmallLink || img.s3MediumLink || '') : '';
        if (imgUrl) {
            return `<div style="
                width: ${markerSize}px;
                height: ${markerSize}px;
                border-radius: 8px;
                border: 3px solid #28a745;
                box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                overflow: hidden;
                position: relative;
                background: #eee;
            ">
                <img src="${imgUrl}" style="
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                " loading="lazy" />
                <div style="
                    position: absolute;
                    bottom: -4px;
                    right: -4px;
                    background: #28a745;
                    color: #fff;
                    border-radius: 50%;
                    width: 20px;
                    height: 20px;
                    font-size: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border: 2px solid white;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.3);
                "><i class="fa-solid fa-newspaper"></i></div>
            </div>`;
        }
        return `<div style="
            width: ${markerSize}px;
            height: ${markerSize}px;
            border-radius: 8px;
            border: 3px solid #28a745;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
            background: #6c757d;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #28a745;
            font-size: 18px;
            position: relative;
        ">
            <i class="fa-solid fa-newspaper"></i>
        </div>`;
    };

    const spiderfy = (group, centerLat, centerLng) => {
        unspiderfy();

        const count = group.length;
        const centerPoint = map.latLngToContainerPoint([centerLat, centerLng]);
        const spiderMarkers = [];
        const spiderLines = [];
        const markerSize = 45;

        group.forEach((newsItem, i) => {
            let offsetX, offsetY;

            if (count <= 12) {
                const radius = count <= 5 ? 60 : (count <= 10 ? 80 : 100);
                const angle = (i / count) * 2 * Math.PI - Math.PI / 2;
                offsetX = radius * Math.cos(angle);
                offsetY = radius * Math.sin(angle);
            } else {
                const angle = i * 0.8;
                const radius = 50 + i * 8;
                offsetX = radius * Math.cos(angle);
                offsetY = radius * Math.sin(angle);
            }

            const targetPoint = L.point(centerPoint.x + offsetX, centerPoint.y + offsetY);
            const targetLatLng = map.containerPointToLatLng(targetPoint);

            const line = L.polyline(
                [[centerLat, centerLng], targetLatLng],
                { color: '#999', weight: 1, opacity: 0.5 },
            );
            line.addTo(map);
            spiderLines.push(line);

            const html = createNewsMarkerHtml(newsItem, markerSize);
            const icon = L.divIcon({
                html,
                className: 'feed-map-marker',
                iconSize: [markerSize, markerSize],
                iconAnchor: [markerSize / 2, markerSize / 2],
            });

            const marker = L.marker(targetLatLng, { icon });
            marker.on('click', (e) => {
                L.DomEvent.stopPropagation(e);
                route(`/race/${newsItem.race}/news/${newsItem.uuidv4}`);
            });
            marker.addTo(map);
            spiderMarkers.push(marker);
        });

        spiderfiedRef.current = {
            markers: spiderMarkers,
            lines: spiderLines,
        };
        spiderfyTimestamp.current = Date.now();
    };

    const buildClusters = () => {
        if (spiderfiedRef.current && Date.now() - spiderfyTimestamp.current < 500) {
            return;
        }
        unspiderfy();
        clearMarkers();

        if (!news || news.length === 0) return;

        let bounds;
        try {
            bounds = map.getBounds();
        } catch (e) {
            return;
        }
        if (!bounds.isValid()) return;

        const cellSize = 80;

        const visibleNews = news.filter(n => {
            const lat = n.location?.latitude;
            const lng = n.location?.longitude;
            if (!lat || !lng) return false;
            return bounds.contains([lat, lng]);
        });

        const cells = {};
        visibleNews.forEach(newsItem => {
            const lat = newsItem.location.latitude;
            const lng = newsItem.location.longitude;
            const point = map.latLngToContainerPoint([lat, lng]);
            const cellX = Math.floor(point.x / cellSize);
            const cellY = Math.floor(point.y / cellSize);
            const key = `${cellX}_${cellY}`;
            if (!cells[key]) {
                cells[key] = [];
            }
            cells[key].push(newsItem);
        });

        Object.values(cells).forEach(group => {
            const representative = group.find(n => {
                const img = n.images?.[0] || n.files?.[0];
                return img && (img.s3SmallLink || img.s3MediumLink);
            }) || group[0];

            const lat = representative.location.latitude;
            const lng = representative.location.longitude;
            const count = group.length;
            const size = count > 1 ? 60 : 50;

            const baseHtml = createNewsMarkerHtml(representative, size);

            let html = baseHtml;
            if (count > 1) {
                const badge = `<div style="
                    position: absolute;
                    top: -6px;
                    right: -6px;
                    background: #28a745;
                    color: white;
                    border-radius: 50%;
                    width: 22px;
                    height: 22px;
                    font-size: 11px;
                    font-weight: bold;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border: 2px solid white;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.3);
                ">${count}</div>`;
                const lastClose = html.lastIndexOf('</div>');
                html = html.slice(0, lastClose) + badge + html.slice(lastClose);
            }

            const icon = L.divIcon({
                html,
                className: 'feed-map-marker',
                iconSize: [size, size],
                iconAnchor: [size / 2, size / 2],
            });

            const marker = L.marker([lat, lng], { icon });

            marker.on('click', (e) => {
                L.DomEvent.stopPropagation(e);
                if (count > 1) {
                    const currentZoom = map.getZoom();

                    if (currentZoom < 12) {
                        map.setView([lat, lng], Math.min(currentZoom + 3, 18));
                    } else {
                        const clusterBounds = L.latLngBounds(
                            group.map(n => [n.location.latitude, n.location.longitude]),
                        );
                        const ne = clusterBounds.getNorthEast();
                        const sw = clusterBounds.getSouthWest();
                        const boundsAreaZero = (
                            Math.abs(ne.lat - sw.lat) < 0.00001
                            && Math.abs(ne.lng - sw.lng) < 0.00001
                        );

                        if (boundsAreaZero || currentZoom >= 14) {
                            spiderfy(group, lat, lng);
                        } else {
                            const targetZoom = map.getBoundsZoom(clusterBounds, false, [50, 50]);
                            if (targetZoom <= currentZoom + 1) {
                                map.setView([lat, lng], Math.min(currentZoom + 3, 18));
                            } else {
                                map.fitBounds(clusterBounds, { padding: [50, 50], maxZoom: 18 });
                            }
                        }
                    }
                } else {
                    route(`/race/${representative.race}/news/${representative.uuidv4}`);
                }
            });

            marker.addTo(map);
            markersRef.current.push(marker);
        });
    };

    const onMapClick = () => {
        if (spiderfiedRef.current && Date.now() - spiderfyTimestamp.current < 500) return;
        unspiderfy();
    };
    map.off('click', onMapClick);
    map.on('click', onMapClick);

    map.off('zoomend moveend', buildClusters);
    map.on('zoomend moveend', buildClusters);

    setTimeout(() => buildClusters(), 100);

    return null;
}

function ClusterLayer({ articles }) {
    const map = useMap();

    const markersRef = { current: [] };
    const spiderfiedRef = { current: null };
    const spiderfyTimestamp = { current: 0 };

    const clearMarkers = () => {
        markersRef.current.forEach(m => map.removeLayer(m));
        markersRef.current = [];
    };

    const unspiderfy = () => {
        if (!spiderfiedRef.current) return;
        spiderfiedRef.current.markers.forEach(m => map.removeLayer(m));
        spiderfiedRef.current.lines.forEach(l => map.removeLayer(l));
        spiderfiedRef.current = null;
    };

    const createArticleMarkerHtml = (article, markerSize) => {
        const img = article.images?.[0] || article.files?.[0];
        const imgUrl = img ? (img.s3SmallLink || img.s3MediumLink || '') : '';
        if (imgUrl) {
            return `<div style="
                width: ${markerSize}px;
                height: ${markerSize}px;
                border-radius: 8px;
                border: 3px solid white;
                box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                overflow: hidden;
                background: #eee;
            ">
                <img src="${imgUrl}" style="
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                " loading="lazy" />
            </div>`;
        }
        return `<div style="
            width: ${markerSize}px;
            height: ${markerSize}px;
            border-radius: 8px;
            border: 3px solid white;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
            background: #6c757d;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-size: 16px;
        ">
            <i class="fa-solid fa-image"></i>
        </div>`;
    };

    const spiderfy = (group, centerLat, centerLng) => {
        unspiderfy();

        const count = group.length;
        const centerPoint = map.latLngToContainerPoint([centerLat, centerLng]);
        const spiderMarkers = [];
        const spiderLines = [];
        const markerSize = 45;

        group.forEach((article, i) => {
            let offsetX, offsetY;

            if (count <= 12) {
                // Circle layout
                const radius = count <= 5 ? 60 : (count <= 10 ? 80 : 100);
                const angle = (i / count) * 2 * Math.PI - Math.PI / 2;
                offsetX = radius * Math.cos(angle);
                offsetY = radius * Math.sin(angle);
            } else {
                // Spiral layout for many items
                const angle = i * 0.8;
                const radius = 50 + i * 8;
                offsetX = radius * Math.cos(angle);
                offsetY = radius * Math.sin(angle);
            }

            const targetPoint = L.point(centerPoint.x + offsetX, centerPoint.y + offsetY);
            const targetLatLng = map.containerPointToLatLng(targetPoint);

            // Draw connecting line
            const line = L.polyline(
                [[centerLat, centerLng], targetLatLng],
                { color: '#999', weight: 1, opacity: 0.5 },
            );
            line.addTo(map);
            spiderLines.push(line);

            // Create individual marker
            const html = createArticleMarkerHtml(article, markerSize);
            const icon = L.divIcon({
                html,
                className: 'feed-map-marker',
                iconSize: [markerSize, markerSize],
                iconAnchor: [markerSize / 2, markerSize / 2],
            });

            const marker = L.marker(targetLatLng, { icon });
            marker.on('click', (e) => {
                L.DomEvent.stopPropagation(e);
                route(`/feed/${article.uuidv4}`);
            });
            marker.addTo(map);
            spiderMarkers.push(marker);
        });

        spiderfiedRef.current = {
            markers: spiderMarkers,
            lines: spiderLines,
        };
        spiderfyTimestamp.current = Date.now();
    };

    const buildClusters = () => {
        // Don't tear down spider if it was just created (protect from zoomend/moveend race)
        if (spiderfiedRef.current && Date.now() - spiderfyTimestamp.current < 500) {
            return;
        }
        unspiderfy();
        clearMarkers();

        if (!articles || articles.length === 0) return;

        let bounds;
        try {
            bounds = map.getBounds();
        } catch (e) {
            // Map container not ready yet — retry on next move/zoom event
            return;
        }
        if (!bounds.isValid()) return;
        const zoom = map.getZoom();

        // Grid cell size in pixels - smaller at higher zoom
        const cellSize = 80;

        const visibleArticles = articles.filter(a => {
            const lat = a.location?.latitude;
            const lng = a.location?.longitude;
            if (!lat || !lng) return false;
            return bounds.contains([lat, lng]);
        });

        // Group articles into grid cells
        const cells = {};
        visibleArticles.forEach(article => {
            const lat = article.location.latitude;
            const lng = article.location.longitude;
            const point = map.latLngToContainerPoint([lat, lng]);
            const cellX = Math.floor(point.x / cellSize);
            const cellY = Math.floor(point.y / cellSize);
            const key = `${cellX}_${cellY}`;
            if (!cells[key]) {
                cells[key] = [];
            }
            cells[key].push(article);
        });

        // Create markers for each cell
        Object.values(cells).forEach(group => {
            // Use the first article with an image as representative, or just the first
            const representative = group.find(a => {
                const img = a.images?.[0] || a.files?.[0];
                return img && (img.s3SmallLink || img.s3MediumLink);
            }) || group[0];

            const lat = representative.location.latitude;
            const lng = representative.location.longitude;

            const img = representative.images?.[0] || representative.files?.[0];
            const imgUrl = img ? (img.s3SmallLink || img.s3MediumLink || '') : '';

            const count = group.length;
            const size = count > 1 ? 60 : 50;

            let html = '';
            if (imgUrl) {
                html = `<div style="
                    width: ${size}px;
                    height: ${size}px;
                    border-radius: 8px;
                    border: 3px solid white;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                    overflow: hidden;
                    position: relative;
                    background: #eee;
                ">
                    <img src="${imgUrl}" style="
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                    " loading="lazy" />
                    ${count > 1 ? `<div style="
                        position: absolute;
                        top: -6px;
                        right: -6px;
                        background: #0d6efd;
                        color: white;
                        border-radius: 50%;
                        width: 22px;
                        height: 22px;
                        font-size: 11px;
                        font-weight: bold;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border: 2px solid white;
                        box-shadow: 0 1px 3px rgba(0,0,0,0.3);
                    ">${count}</div>` : ''}
                </div>`;
            } else {
                html = `<div style="
                    width: ${size}px;
                    height: ${size}px;
                    border-radius: 8px;
                    border: 3px solid white;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                    background: #6c757d;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-size: 18px;
                    position: relative;
                ">
                    <i class="fa-solid fa-image"></i>
                    ${count > 1 ? `<div style="
                        position: absolute;
                        top: -6px;
                        right: -6px;
                        background: #0d6efd;
                        color: white;
                        border-radius: 50%;
                        width: 22px;
                        height: 22px;
                        font-size: 11px;
                        font-weight: bold;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border: 2px solid white;
                        box-shadow: 0 1px 3px rgba(0,0,0,0.3);
                    ">${count}</div>` : ''}
                </div>`;
            }

            const icon = L.divIcon({
                html,
                className: 'feed-map-marker',
                iconSize: [size, size],
                iconAnchor: [size / 2, size / 2],
            });

            const marker = L.marker([lat, lng], { icon });

            marker.on('click', (e) => {
                L.DomEvent.stopPropagation(e);
                if (count > 1) {
                    const currentZoom = map.getZoom();

                    if (currentZoom < 12) {
                        // At low zoom levels, always zoom in first
                        map.setView([lat, lng], Math.min(currentZoom + 3, 18));
                    } else {
                        // At high zoom, check if zooming would help
                        const clusterBounds = L.latLngBounds(
                            group.map(a => [a.location.latitude, a.location.longitude]),
                        );
                        const ne = clusterBounds.getNorthEast();
                        const sw = clusterBounds.getSouthWest();
                        const boundsAreaZero = (
                            Math.abs(ne.lat - sw.lat) < 0.00001
                            && Math.abs(ne.lng - sw.lng) < 0.00001
                        );

                        if (boundsAreaZero || currentZoom >= 14) {
                            // Same location or max zoom — spiderfy
                            spiderfy(group, lat, lng);
                        } else {
                            const targetZoom = map.getBoundsZoom(clusterBounds, false, [50, 50]);
                            if (targetZoom <= currentZoom + 1) {
                                map.setView([lat, lng], Math.min(currentZoom + 3, 18));
                            } else {
                                map.fitBounds(clusterBounds, { padding: [50, 50], maxZoom: 18 });
                            }
                        }
                    }
                } else {
                    route(`/feed/${representative.uuidv4}`);
                }
            });

            marker.addTo(map);
            markersRef.current.push(marker);
        });
    };

    // Click on map background to close spiderfied state
    const onMapClick = () => {
        if (spiderfiedRef.current && Date.now() - spiderfyTimestamp.current < 500) return;
        unspiderfy();
    };
    map.off('click', onMapClick);
    map.on('click', onMapClick);

    // Build clusters on map events
    map.off('zoomend moveend', buildClusters);
    map.on('zoomend moveend', buildClusters);

    // Initial build after a short delay to let map settle
    setTimeout(() => buildClusters(), 100);

    return null;
}

@observer
class ArticleFeedMap extends Component {
    constructor(props) {
        super(props);
        this.state = {
            articles: [],
            races: [],
            news: [],
            loading: true,
        };
        this.mapRef = null;
    }

    async componentDidMount() {
        await this.loadData();
    }

    loadData = async () => {
        this.setState({ loading: true });
        const [articleResponse, raceResponse, newsResponse] = await Promise.all([
            util.fetchApi('/api/articles/', { method: 'GET' }, {
                category: 'feed',
                hasLocation: 1,
                limit: 500,
                hostname: window.location.hostname,
            }),
            util.fetchApi('/api/races/public/map', { method: 'GET' }, {
                limit: 500,
                showAll: 1,
            }),
            // TODO: Disabled - response too large (3.7MB). Need to add field projection or pagination.
            // util.fetchApi('/api/news/', { method: 'GET' }, {
            //     hasLocation: 1,
            //     limit: 500,
            // }),
            Promise.resolve({ data: [] }),
        ]);
        const articles = articleResponse?.data || [];
        const allRaces = raceResponse?.data || [];
        const news = newsResponse?.data || [];
        // Use startLatlng if set, otherwise fall back to startCheckpoint from race classes
        const races = allRaces
            .map(r => {
                if (typeof r.startLatlng?.[0] === 'number' && typeof r.startLatlng?.[1] === 'number') return r;
                if (r.startCheckpoint?.lat && r.startCheckpoint?.lng) {
                    return { ...r, startLatlng: [parseFloat(r.startCheckpoint.lat), parseFloat(r.startCheckpoint.lng)] };
                }
                return r;
            })
            .filter(r => typeof r.startLatlng?.[0] === 'number' && typeof r.startLatlng?.[1] === 'number'
                && isFinite(r.startLatlng[0]) && isFinite(r.startLatlng[1]));
        this.setState({ articles, races, news, loading: false });

        this.fitMapBounds(articles, races, news);
    }

    fitMapBounds = (articles, races, news = []) => {
        if (!this.mapRef) return;
        const points = [];
        articles
            .filter(a => a.location?.latitude && a.location?.longitude)
            .forEach(a => points.push([a.location.latitude, a.location.longitude]));
        races
            .filter(r => typeof r.startLatlng?.[0] === 'number' && typeof r.startLatlng?.[1] === 'number'
                && isFinite(r.startLatlng[0]) && isFinite(r.startLatlng[1]))
            .forEach(r => points.push([r.startLatlng[0], r.startLatlng[1]]));
        news
            .filter(n => n.location?.latitude && n.location?.longitude)
            .forEach(n => points.push([n.location.latitude, n.location.longitude]));
        if (points.length > 0) {
            const bounds = L.latLngBounds(points);
            if (bounds.isValid()) {
                this.mapRef.fitBounds(bounds, { padding: [30, 30] });
            }
        }
    }

    setMapRef = (ref) => {
        if (ref) {
            this.mapRef = ref;
            const { articles, races, news } = this.state;
            this.fitMapBounds(articles, races, news);
        }
    }

    render() {
        const { articles, races, news, loading } = this.state;

        if (loading) {
            return (
                <div class='d-flex justify-content-center align-items-center' style='height: 400px;'>
                    <div class='spinner-border text-primary' role='status'>
                        <span class='visually-hidden'>Loading...</span>
                    </div>
                </div>
            );
        }

        const geoArticles = articles.filter(a => a.location?.latitude && a.location?.longitude);
        const geoRaces = races.filter(r => typeof r.startLatlng?.[0] === 'number' && typeof r.startLatlng?.[1] === 'number'
            && isFinite(r.startLatlng[0]) && isFinite(r.startLatlng[1]));
        const geoNews = news.filter(n => n.location?.latitude && n.location?.longitude);

        if (geoArticles.length === 0 && geoRaces.length === 0 && geoNews.length === 0) {
            return (
                <div class='d-flex justify-content-center align-items-center text-body-secondary' style='height: 200px;'>
                    <div class='text-center'>
                        <i class='fa-solid fa-map-location-dot fa-2x mb-2' />
                        <div>No geolocated posts yet</div>
                    </div>
                </div>
            );
        }

        // Default center: first article or race location
        let defaultCenter = [65, 15]; // Fallback: Norway
        if (geoArticles.length > 0) {
            defaultCenter = [geoArticles[0].location.latitude, geoArticles[0].location.longitude];
        } else if (geoRaces.length > 0) {
            defaultCenter = [geoRaces[0].startLatlng[0], geoRaces[0].startLatlng[1]];
        }

        return (
            <div style='position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 1;'>
                <div style='position: absolute; top: 95px; left: 50%; transform: translateX(-50%); z-index: 1000;'>
                    <div class='bg-body rounded-pill shadow px-3 py-2 d-flex flex-row align-items-center' style='gap: 0.75rem;'>
                        <span
                            class='text-body-secondary'
                            style='cursor: pointer;'
                            onClick={() => route('/feed')}
                        ><Text id='article.user-feed'>Feed</Text></span>
                        <span class='text-body-tertiary' style='font-size: 0.8em;'>|</span>
                        <span
                            class='text-body-secondary'
                            style='cursor: pointer;'
                            onClick={() => route('/feed/video')}
                        ><Text id='article.videos'>Videos</Text></span>
                        <span class='text-body-tertiary' style='font-size: 0.8em;'>|</span>
                        <span
                            class='text-body fw-bold'
                        ><i class='fa-solid fa-map-location-dot' /></span>
                    </div>
                </div>
                <MapContainer
                    center={defaultCenter}
                    zoom={5}
                    scrollWheelZoom={true}
                    style={{ height: '100%', width: '100%' }}
                    ref={this.setMapRef}
                >
                    <TileLayer
                        attribution='&copy; <a href="https://statkart.no">Kartverket</a>'
                        url='https://cache.kartverket.no/v1/wmts/1.0.0/topograatone/default/webmercator/{z}/{y}/{x}.png'
                    />
                    <ClusterLayer articles={geoArticles} />
                    <RaceLayer races={geoRaces} />
                    <NewsLayer news={geoNews} />
                </MapContainer>
            </div>
        );
    }
}

export default ArticleFeedMap;
